import { FC, FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { FormLayout } from "@/components/Forms";
import Button from "@/components/Button";
import FormField from "@/components/Forms/FormField";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import {
  ProductionSceneLocationResource,
  ProductionSceneResource,
} from "@/types/resources";

type FormDataT = {
  title: string;
  address: string;
  production_scene_id: number;
  date_time?: string;
  note?: string;
};

interface Props {
  onClose: () => void;
  scenes?: number[];
  location?: ProductionSceneLocationResource;
  scene?: ProductionSceneResource;
}

const LocationsModal: FC<Props> = ({ onClose, scenes, location, scene }) => {
  const { projectId } = useParams();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    clearErrors,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      title: location?.title ?? "",
      address: location?.address ?? "",
      date_time: location?.time ?? "",
      note: location?.note ?? "",
      production_scene_id: scene?.id,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        if (location) {
          await api.put<never, { id: number }>(
            `/v1/production/scenes/locations/${location.id}`,
            data
          );
        } else {
          await api.post<never, ProductionSceneLocationResource>(
            "/v1/production/scenes/locations",
            data
          );
        }

        queryClient.invalidateQueries(["production_projects", { projectId }]);

        alerts.success(
          `Location has been successfully ${!!location ? "updated" : "created"}
          `
        );

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        heading={`${!location ? "Add" : "Edit"} location`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                name="title"
                label="Element"
                id="title"
              />
              {!!scenes?.length && (
                <FormField<FormDataT, number>
                  control={control}
                  name="production_scene_id"
                  label="Scene number"
                  id="scene_id"
                  type="select"
                  options={scenes.map((scene, index) => {
                    return {
                      label: (index + 1).toString(),
                      value: scene,
                    };
                  })}
                />
              )}
              <FormField<FormDataT>
                control={control}
                name="address"
                label="Address"
                id="address"
                suggestionsEndpoint="/v1/production/scenes/locations"
              />
              <FormField<FormDataT>
                control={control}
                name="date_time"
                label="Start Date & Time"
                id="date"
                type="datepicker"
                datepickerType="datetime"
              />
              <FormField<FormDataT>
                control={control}
                name="note"
                label="Notes"
                type="textarea"
                id="notes"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default LocationsModal;
